import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  LoadingState,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createTasksApi } from "@/modules/tasks/api";
import type { Me } from "@/modules/tasks/types";
import { createApprovalsApi } from "../api";
import { APPROVAL_TYPES, approvalStatusTone, formatLabel } from "../labels";
import type { ApprovalDto, ListParams } from "../types";
import { CreateApprovalDialog, type CreateApprovalPrefill } from "../components/ApprovalDialogs";

type View = "decide" | "mine" | "open" | "decided" | "closed";

const VIEWS: { id: View; label: string; empty: string }[] = [
  { id: "decide", label: "Needs my decision", empty: "Nothing is waiting on you." },
  { id: "mine", label: "My requests", empty: "You haven't requested any approvals." },
  { id: "open", label: "All open", empty: "No approvals are waiting on anyone." },
  { id: "decided", label: "Decided", empty: "Nothing has been decided yet." },
  { id: "closed", label: "Withdrawn or expired", empty: "No withdrawn or expired requests." },
];

const PAGE = 25;

/** Levels the viewer can decide: a manager sees manager requests, an admin also dual, a super admin all. */
function decidableLevels(me: Me | null): string | undefined {
  if (!me?.approval_authority) return "manager";
  if (me.approval_authority === "super_admin") return undefined;
  if (me.approval_authority === "dual") return "manager,dual";
  return "manager";
}

function viewParams(view: View, me: Me | null): ListParams {
  switch (view) {
    case "decide": {
      const levels = decidableLevels(me);
      return {
        status: "requested,pending",
        awaiting_my_decision: true,
        sort: "requested_at",
        ...(levels ? { authority_level: levels } : {}),
      };
    }
    case "mine":
      return { ...(me ? { requester_user_id: me.user_id } : {}), sort: "-requested_at" };
    case "open":
      return { status: "requested,pending", sort: "requested_at" };
    case "decided":
      return { status: "approved,rejected,returned_for_changes", sort: "-requested_at" };
    default:
      return { status: "withdrawn,expired", sort: "-requested_at" };
  }
}

export function expiryLabel(expiresAt: string | null, status: string): string {
  if (!expiresAt) return "—";
  if (status !== "requested" && status !== "pending") return new Date(expiresAt).toLocaleDateString();
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Expiring now";
  const h = Math.round(ms / 3_600_000);
  return h < 48 ? `in ${Math.max(h, 1)}h` : `in ${Math.round(h / 24)}d`;
}

function ApprovalsCenter() {
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApiClient();
  const api = useMemo(() => createApprovalsApi(client), [client]);
  const tasksApi = useMemo(() => createTasksApi(client), [client]);
  const [params, setParams] = useSearchParams();

  const view = (params.get("view") as View | null) ?? "decide";
  const type = params.get("approval_type") ?? "";
  const [me, setMe] = useState<Me | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);
  const [rows, setRows] = useState<ApprovalDto[]>([]);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [decideCount, setDecideCount] = useState<number | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const createOpen = params.get("new") === "1";
  const prefill: CreateApprovalPrefill = useMemo(
    () => ({
      ...(params.get("subject_type") ? { subject_type: params.get("subject_type")! } : {}),
      ...(params.get("subject_id") ? { subject_id: params.get("subject_id")! } : {}),
      ...(params.get("authority_level") ? { authority_level: params.get("authority_level")! } : {}),
      ...(params.get("prefill_type") ? { approval_type: params.get("prefill_type")! } : {}),
    }),
    [params],
  );

  function update(patch: Record<string, string>) {
    const n = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) n.set(k, v);
      else n.delete(k);
    }
    setParams(n);
    setCursors([undefined]);
  }

  useEffect(() => {
    tasksApi
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setMeLoaded(true));
  }, [tasksApi]);

  const cursor = cursors[cursors.length - 1];

  const load = useCallback(async () => {
    if (!meLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const base = viewParams(view, me);
      const decideBase = viewParams("decide", me);
      const [list, decide] = await Promise.all([
        api.listApprovals({ ...base, limit: PAGE, ...(cursor ? { cursor } : {}), ...(type ? { approval_type: type } : {}) }),
        api.listApprovals({ ...decideBase, limit: 1 }),
      ]);
      setRows(list.items);
      setNext(list.next_cursor ?? null);
      setDecideCount(Object.values(decide.meta?.status_counts ?? {}).reduce((a, b) => a + b, 0));
      setForbidden(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setForbidden(true);
      setError(apiErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, view, me, meLoaded, cursor, type]);

  useEffect(() => {
    void load();
  }, [load]);

  if (forbidden) {
    return (
      <Stack gap={24}>
        <SectionHeader title="Approvals" />
        <EmptyState
          title="You don't have access to Approvals"
          description="Approvals need the Operations › View permission to read and Operations › Manage to request or decide. A Super Admin can grant it under System › Access."
          action={<Button variant="secondary" onClick={() => void navigate("/app/system/access")}>Open Access</Button>}
        />
      </Stack>
    );
  }

  const table = (
    <Stack gap={12}>
      {loading ? <LoadingState message="Loading approvals…" /> : null}
      {!loading && error ? (
        <Alert tone="danger" title="Could not load approvals">
          {error}{" "}
          <Button size="sm" variant="ghost" onClick={() => void load()}>Retry</Button>
        </Alert>
      ) : null}
      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          title={type ? "Nothing matches this filter" : "Nothing here"}
          description={type ? "Clear the filter to see every kind." : VIEWS.find((v) => v.id === view)?.empty}
          action={
            type ? (
              <Button variant="secondary" onClick={() => update({ approval_type: "" })}>Clear filter</Button>
            ) : view === "mine" ? (
              <Button onClick={() => update({ new: "1" })}>Request an approval</Button>
            ) : undefined
          }
        />
      ) : null}
      {!loading && rows.length > 0 ? (
        <Table>
          <Thead>
            <Tr>
              <Th>Request</Th>
              <Th>Status</Th>
              <Th>Who decides</Th>
              <Th>Requested by</Th>
              <Th>Requested</Th>
              <Th>Expires</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.id} style={{ cursor: "pointer" }} onClick={() => void navigate(`/app/approvals/${row.id}`)}>
                <Td>
                  <Stack gap={4}>
                    <Text role="label" as="span">{row.summary ?? formatLabel(row.approval_type)}</Text>
                    <Text role="caption" color="secondary" as="span">
                      {formatLabel(row.approval_type)} · {formatLabel(row.subject_type)}
                    </Text>
                  </Stack>
                </Td>
                <Td>
                  <StatusChip tone={approvalStatusTone(row.status)}>{formatLabel(row.status)}</StatusChip>
                </Td>
                <Td>
                  {formatLabel(row.authority_level)}
                  {row.required_decision_count > 1 ? ` · ${row.required_decision_count} approvers` : ""}
                </Td>
                <Td>{row.requester_user_id === me?.user_id ? "Me" : (row.requester_name ?? "Staff member")}</Td>
                <Td>{new Date(row.requested_at).toLocaleString()}</Td>
                <Td>{expiryLabel(row.expires_at, row.status)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : null}
      {!loading && rows.length > 0 ? (
        <ActionBar
          left={<Text role="small" color="secondary" as="span">Page {cursors.length}</Text>}
          right={
            <Flex gap={8}>
              <Button size="sm" variant="secondary" disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>Previous</Button>
              <Button size="sm" variant="secondary" disabled={!next} onClick={() => next && setCursors((c) => [...c, next])}>Next</Button>
            </Flex>
          }
        />
      ) : null}
    </Stack>
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Approvals"
        description="Maker-checker for risky actions: whoever raises a request can never decide it, the decider must hold the level the request needs, every rejection says why, and undecided requests expire. Business, finance and access actions that need approval ask for one of these."
        actions={
          <Flex gap={8} wrap>
            <Button onClick={() => update({ new: "1" })}>Request an approval</Button>
            <Button variant="secondary" onClick={() => void load()}>Refresh</Button>
          </Flex>
        }
      />
      {me?.approval_authority ? (
        <Text role="small" color="secondary" as="p">
          You can decide {me.approval_authority === "super_admin" ? "any request" : me.approval_authority === "dual" ? "manager and dual requests" : "manager-level requests"}.
        </Text>
      ) : null}
      <Filters>
        <Select
          label="Kind"
          value={type}
          onChange={(e) => update({ approval_type: e.target.value })}
          options={[{ value: "", label: "All kinds" }, ...APPROVAL_TYPES.filter((t) => t !== "future_governed_type").map((t) => ({ value: t, label: formatLabel(t) }))]}
        />
      </Filters>
      <Tabs
        value={view}
        onValueChange={(id) => update({ view: id })}
        items={VIEWS.map((v) => ({
          id: v.id,
          label: v.id === "decide" && decideCount !== null ? `${v.label} (${decideCount})` : v.label,
          content: table,
        }))}
      />
      <CreateApprovalDialog
        open={createOpen}
        prefill={prefill}
        busy={createBusy}
        error={createError}
        onClose={() => {
          setCreateError(null);
          update({ new: "", subject_type: "", subject_id: "", authority_level: "", prefill_type: "" });
        }}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const r = await api.createApproval(body);
            toast.push({ title: "Sent for approval", tone: "success" });
            void navigate(`/app/approvals/${r.approval.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function ApprovalsHubPage() {
  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Approvals" }]);
  return <ApprovalsCenter />;
}

export function ApprovalsQueuePage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Approvals", to: "/app/approvals" },
    { label: "Queue" },
  ]);
  return <ApprovalsCenter />;
}
